import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { useState, useContext } from 'react';

import Button from '@/ui/components/Button';

import { BillingFlagReasonContext } from '../contexts/BillingFlagReasonContext';
import { BillingReportContext } from '../contexts/BillingReportProvider';
import { BillingReportRow, Reason } from '../types';
import { BillingItemFlagType } from '../types/billing_item_flag';

import ReasonDropdown from './BillingItemFlagReasonDropdown';

interface Props {
  rows: BillingReportRow[];
  onDismiss: () => void;
  onSaveHold: (reason: string, note: string, rows: BillingReportRow[]) => void;
  onClearFlag?: (type: string, onlyMyFlags: boolean, rows: BillingReportRow[]) => Promise<void>;
}

const ManualHoldServiceLines = ({
  rows,
  onSaveHold,
  onDismiss,
  onClearFlag
}: Props): JSX.Element => {
  const [manualHoldReasonNote, setManualHoldReasonNote] = useState('');
  const [loading, setLoading] = useState({
    submitting: false,
    clearing: false
  });
  const { setErrorMessage } = useContext(BillingReportContext);
  const [selectedReason, setSelectedReason] = useState<Reason | null>(null);
  const { billingItemFlagReasons } = useContext(BillingFlagReasonContext);
  const manualHoldReasons = billingItemFlagReasons['manually_held'] || [];

  const handleOnClick = async () => {
    if (!selectedReason) {
      return;
    }

    setLoading({ ...loading, submitting: true });
    try {
      await onSaveHold(selectedReason.name, manualHoldReasonNote, rows);
      setTimeout(() => {
        onDismiss();
      }, 500);
    } catch (error) {
      console.error('Error setting manual hold for selected rows: ', error);
      setErrorMessage('Failed to set manual hold for selected rows');
    } finally {
      setLoading({ ...loading, submitting: false });
    }
  };

  const clearFlags = async (type: string, { onlyMyFlags }: { onlyMyFlags: boolean }) => {
    if (onClearFlag) {
      setLoading({ ...loading, clearing: true });
      await onClearFlag(type, onlyMyFlags, rows);
      setLoading({ ...loading, clearing: false });
    }
  };

  return (
    <div className="my-2 max-w-5xl bg-white p-6 rounded shadow space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl">Manually Hold Selected Rows</h2>
      </div>

      <div className="relative my-8">
        <ReasonDropdown reasons={manualHoldReasons} onReasonChange={setSelectedReason} />
        {!selectedReason && (
          <p className="mt-1 text-sm text-red-500">Selecting a reason is required</p>
        )}
      </div>

      <div>
        <textarea
          data-testid="manual-hold-reason-note"
          className="p-3 w-full text-sm text-gray-700 border border-gray-300 rounded focus:outline-none focus:border-blue-500 transition duration-200 ease-in-out resize-none"
          value={manualHoldReasonNote}
          onChange={(e) => setManualHoldReasonNote(e.target.value)}
          placeholder="Enter a note for the manual hold"
        />
      </div>
      <Button className=" mr-2" onClick={onDismiss} type="button" color="grey">
        Cancel
      </Button>
      <Button
        dataTestId="hold-submit-btn"
        disabled={!selectedReason}
        color="green"
        className=" mr-2"
        onClick={handleOnClick}
        type="button">
        {loading.submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Submit Manual Hold'}
      </Button>

      <Button
        className="mr-2 flex items-center"
        color="red"
        onClick={() => {
          clearFlags(BillingItemFlagType.IGNORE_ERROR, {
            onlyMyFlags: false
          });
        }}
        disabled={loading.clearing}
        type="button">
        {loading.clearing ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Clearing...
          </>
        ) : (
          'Clear Holds'
        )}
      </Button>
    </div>
  );
};

export default ManualHoldServiceLines;
